import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, Heart, CalendarCheck, User, Globe, LayoutDashboard } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../contexts/I18nContext";
import { useSite } from "../../contexts/SiteContext";
import { Logo } from "./Logos";
import "./site.css";

const Toggle = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-full bg-slate-100 p-0.5" data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testid}-${o.toLowerCase()}`}
        className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${value === o ? "bg-[#0A1628] text-white" : "text-slate-500 hover:text-[#0A1628]"}`}>
        {o}
      </button>
    ))}
  </div>
);

export const SiteToggles = ({ prefix = "" }) => {
  const { lang, changeLang } = useI18n();
  const { cur, setCur } = useSite();
  return (
    <div className="flex items-center gap-2">
      <Globe className="w-4 h-4 text-slate-400" />
      <Toggle options={["EN", "BG"]} value={lang.toUpperCase()} onChange={(v) => changeLang(v.toLowerCase())} testid={`${prefix}lang-toggle`} />
      <Toggle options={["USD", "EUR"]} value={cur} onChange={setCur} testid={`${prefix}currency-toggle`} />
    </div>
  );
};

const Header = () => {
  const { user } = useAuth();
  const { L } = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const links = [
    { to: "/find-coaches", label: L("Find a coach", "Намери треньор"), id: "find" },
    { to: "/#sports", label: L("Sports", "Спортове"), id: "sports" },
    { to: "/for-coaches", label: L("For coaches", "За треньори"), id: "coaches" },
    { to: "/for-coaches#academies", label: L("Academies", "Академии"), id: "academies" },
  ];
  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70" data-testid="site-header">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-4 sm:px-8 h-16 lg:h-[72px]">
        <Link to="/" data-testid="header-logo"><Logo variant="velocity" textClass="text-lg sm:text-xl" size={30} /></Link>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => <Link key={l.id} to={l.to} className="text-[15px] font-medium text-[#0A1628] hover:text-[#1D4ED8] transition-colors" data-testid={`header-link-${l.id}`}>{l.label}</Link>)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:flex"><SiteToggles /></div>
          {user ? (
            <Link to="/dashboard" className="otp-btn inline-flex items-center gap-2 text-sm font-semibold px-4 sm:px-5 py-2.5 rounded-full bg-[#0A1628] text-white" data-testid="header-dashboard"><LayoutDashboard className="w-4 h-4" /> <span className="hidden sm:inline">{L("Dashboard", "Табло")}</span></Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex otp-btn text-sm font-semibold px-4 py-2 rounded-full text-[#0A1628] hover:bg-slate-100" data-testid="header-signin">{L("Sign in", "Вход")}</Link>
              <Link to="/register?role=coach" className="hidden sm:inline-flex otp-btn otp-shine text-sm font-semibold px-5 py-2.5 rounded-full bg-[#1D4ED8] text-white hover:bg-[#1E40AF]" data-testid="header-become-coach">{L("Become a coach", "Стани треньор")}</Link>
            </>
          )}
          <button type="button" onClick={() => setOpen(!open)} className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-slate-200 text-[#0A1628]" data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 pb-6 pt-2 otp-rise" data-testid="mobile-menu">
          {links.map((l) => <Link key={l.id} to={l.to} className="block py-4 otp-display text-3xl uppercase text-[#0A1628] border-b border-slate-100">{l.label}</Link>)}
          {!user && <Link to="/login" className="block py-4 otp-display text-3xl uppercase text-[#1D4ED8] border-b border-slate-100" data-testid="mobile-signin">{L("Sign in", "Вход")}</Link>}
          <div className="pt-5"><SiteToggles prefix="mobile-" /></div>
        </div>
      )}
    </header>
  );
};

const MobileTabs = () => {
  const { user } = useAuth();
  const { L } = useSite();
  const tabs = [
    { to: "/find-coaches", icon: Search, label: L("Explore", "Търси"), id: "explore" },
    { to: user ? "/favorites" : "/login", icon: Heart, label: L("Saved", "Любими"), id: "saved" },
    { to: user ? "/bookings" : "/login", icon: CalendarCheck, label: L("Bookings", "Резервации"), id: "bookings" },
    { to: user ? "/dashboard" : "/login", icon: User, label: L("Profile", "Профил"), id: "profile" },
  ];
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 grid grid-cols-4 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tab-bar">
      {tabs.map(({ to, icon: I, label, id }) => (
        <Link key={id} to={to} className="flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium text-slate-500 hover:text-[#1D4ED8]" data-testid={`mobile-tab-${id}`}><I className="w-5 h-5" /> {label}</Link>
      ))}
    </nav>
  );
};

const Footer = () => {
  const { L } = useSite();
  const cols = [
    [L("Athletes & parents", "Спортисти и родители"), [[L("Find a coach", "Намери треньор"), "/find-coaches"], [L("Academies", "Академии"), "/find-coaches?tab=academies"], [L("Cancellation policy", "Политика за отмяна"), "/policies"], [L("Safety & privacy", "Сигурност"), "/policies#privacy"]]],
    [L("Coaches", "Треньори"), [[L("Become a coach", "Стани треньор"), "/register?role=coach"], [L("How it works", "Как работи"), "/for-coaches"], [L("Academy plans", "Планове за академии"), "/for-coaches#academies"]]],
    ["ONTHEPITCH", [[L("Sign in", "Вход"), "/login"], [L("Create account", "Регистрация"), "/register"], [L("Terms", "Условия"), "/policies#terms"]]],
  ];
  return (
    <footer className="bg-[#0A1628] text-white mt-24 pb-24 lg:pb-0 relative overflow-hidden otp-grain" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 pt-20 pb-10 grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Logo variant="velocity" dark size={36} textClass="text-2xl" />
          <p className="mt-5 text-white/60 max-w-sm">{L("The premium marketplace for private sports coaching. Born in Los Angeles.", "Премиум платформа за индивидуални тренировки. Родена в Лос Анджелис.")}</p>
        </div>
        {cols.map(([h, items]) => (
          <div key={h}>
            <div className="otp-label text-white/40 mb-5">{h}</div>
            <ul className="space-y-3">{items.map(([label, to]) => <li key={label}><Link to={to} className="text-white/80 hover:text-white transition-colors">{label}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-6 border-t border-white/10 flex flex-wrap gap-4 justify-between text-sm text-white/50">
        <span>© {new Date().getFullYear()} ONTHEPITCH · Los Angeles, CA</span>
        <span>{L("Payments secured by Stripe", "Плащания чрез Stripe")}</span>
      </div>
      <div aria-hidden className="otp-display uppercase italic text-white/[0.04] text-[22vw] leading-none -mb-[4vw] select-none whitespace-nowrap px-4">ONTHEPITCH</div>
    </footer>
  );
};

const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 80);
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

export default function SiteLayout({ children, footer = true }) {
  return (
    <div className="otp-root min-h-screen" data-testid="site-root">
      <ScrollTop />
      <Header />
      <main>{children}</main>
      {footer && <Footer />}
      <MobileTabs />
    </div>
  );
}
